import {
  Anchor,
  Avatar,
  Box,
  COLOR_PALETTE,
  Divider,
  Ellipsis,
  Flex,
  OptimizeImage,
  Stack,
  Text,
} from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';

type Props = {
  course?: {
    title: string;
    href: string;
    thumbnailUrl: string;
  };
  writer?: {
    label: string;
    name: string;
    href: string;
    thumbnailUrl: string;
  };
};

export default function CourseInformation({ course, writer }: Props) {
  if (!course && !writer) {
    return null;
  }

  return (
    <Stack
      px={16}
      py={10}
      css={{
        border: `1px solid ${COLOR_PALETTE.gray[2]}`,
        borderRadius: 16,
        overflow: 'hidden',
      }}
    >
      {course && <CourseSection course={course} />}
      {course && writer && <Divider my={10} color="gray.3" />}
      {writer && <WriterSection writer={writer} />}
    </Stack>
  );
}

function CourseSection({ course }: { course: Required<Props>['course'] }) {
  const { t } = useTranslation();

  return (
    <Anchor href={course.href}>
      <Flex
        gap={12}
        align="center"
        css={{
          '&:hover': {
            '.course-title': {
              textDecoration: 'underline',
              textUnderlinePosition: 'under',
            },
          },
        }}
      >
        <Box
          w={56}
          h={36}
          css={{
            width: 56,
            height: 36,
            flexShrink: 0,
            borderRadius: 4,
            overflow: 'hidden',
            img: {
              width: '100%',
              height: '100%',
            },
          }}
        >
          <OptimizeImage
            w={112}
            src={course.thumbnailUrl}
            alt={t('{{course.title}} 강의 썸네일', {
              'course.title': course.title,
            })}
          />
        </Box>
        <Ellipsis lineClamp={1}>
          <Text size="sm" weight={700} color="dark" className="course-title">
            {course.title}
          </Text>
        </Ellipsis>
      </Flex>
    </Anchor>
  );
}

function WriterSection({ writer }: { writer: Required<Props>['writer'] }) {
  const { t } = useTranslation();

  return (
    <Flex gap={12} align="center">
      <Text size="xs" color="gray.6" weight={400}>
        {writer.label}
      </Text>
      <Link href={writer.href}>
        <Flex
          gap={8}
          align="center"
          css={{
            '&:hover': {
              '.writer-name': {
                textDecoration: 'underline',
                textUnderlinePosition: 'under',
              },
            },
          }}
        >
          <Avatar
            size="sm"
            radius="xl"
            alt={t('{{writer.name}} 프로필 사진', {
              'writer.name': writer.name,
            })}
            src={writer.thumbnailUrl}
          />
          <Text size="sm" color="gray.7" weight={700} className="writer-name">
            {writer.name}
          </Text>
        </Flex>
      </Link>
    </Flex>
  );
}
